import React, { useMemo, useState } from 'react';
import { Badge, Box, makeStyles } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import { i18n } from '@lingui/core';
import { filter, get, intersection, isEmpty, merge, sortBy } from 'lodash';
import { t, Trans } from '@lingui/macro';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import { SORT_ASC } from '../../../utilities/FormUtilities';
import DataTable from '../../data-table/DataTable';
import { AGENT_MAX_GLOBAL_TESTS, AGENT_TYPE_GLOBAL, AGENTS_FUNCTION_NAME } from './AgentConstants';
import FormSection from '../../form/FormSection';
import {
    NAME_FIELD,
    SEM_FIELD,
    TAGS_FIELD,
    TEST_STATUS_FIELD,
    TEST_STATUS_RUNNING,
} from '../service-tests/ServiceTestAdminConstants';
import { useDataService } from '../../../services/ApiDataService';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { MaterialUiIcon } from '../../icon';
import { convertLoadParamsToQueryParams } from '../../data-table/Loaders';
import { RowFlexLayout } from '../../layout';
import { AdminPages, buildRootPageTarget } from '../AdminLinkUtils';
import { formatTags } from '../../../utilities/StringUtilities';
import NgpFieldHelpTip from '../../form/NgpFieldHelpTip';

function getAssignmentType(row, agentId, agentTags) {
    if (row?.agents?.ids?.length && row.agents.ids.indexOf(agentId) >= 0) {
        return t`select nPoints`;
    }
    if (row?.agents?.tags?.length) {
        const sortedOverlap = sortBy(intersection(row.agents.tags, agentTags));
        if (sortedOverlap.length) {
            return t`tagged nPoints (matches ${sortedOverlap.join(', ')})`;
        }
    }
    return t`all nPoints`;
}

function buildTypeTestDetailsLink(row) {
    const typeGroup = encodeURIComponent(get(row, 'type', ''));
    const testId = encodeURIComponent(get(row, '_id', ''));
    return `${buildRootPageTarget(
        AdminPages.ServiceTests
    )}/${typeGroup}/tests/${testId}?returnTo=testTypes.errorDetails`;
}

function useTestColumns(agentId, agentType, agentTags) {
    return useMemo(() => {
        return [
            {
                type: COLUMN_TYPES.ICON,
                title: t`Type`,
                mapping: 'testTypeObject',
                sortable: false,
                width: 74,
                icons: [
                    {
                        getIcon: (row) => <MaterialUiIcon icon={row?.testTypeObject?.details?.icon} />,
                        getTooltip: (row) => row?.testTypeObject?.details?.title,
                    },
                ],
            },
            ...(agentType === AGENT_TYPE_GLOBAL
                ? [
                      {
                          type: COLUMN_TYPES.ICON,
                          title: t`Running`,
                          mapping: 'runningOnGlobalnPoint',
                          sortable: false,
                          width: 74,
                          icons: [
                              {
                                  getIcon: (row) => {
                                      const icon = row?.runningOnGlobalnPoint ? 'check_circle' : 'cancel';
                                      const color = row?.runningOnGlobalnPoint ? 'primary' : 'disabled';
                                      return <Icon color={color}>{icon}</Icon>;
                                  },
                                  getTooltip: (row) => {
                                      const assignmentType = getAssignmentType(row, agentId, agentTags);
                                      return row?.runningOnGlobalnPoint
                                          ? t`Test is running. Run On: ${assignmentType}.`
                                          : t`Test is not running, exceeds the test limit of ${AGENT_MAX_GLOBAL_TESTS}. Run On: ${assignmentType}.`;
                                  },
                              },
                          ],
                      },
                  ]
                : []),
            {
                type: COLUMN_TYPES.LINK,
                title: t`Name`,
                sortable: true,
                mapping: NAME_FIELD,
                width: 300,
                link: (row) => ({ target: buildTypeTestDetailsLink(row) }),
            },
            {
                type: COLUMN_TYPES.TEXT,
                mapping: SEM_FIELD,
                title: t`Smart Edge Monitoring`,
                align: 'center',
                sortable: true,
                formatter: (value) => {
                    return value === true ? t`On` : t`Off`;
                },
                width: 195,
            },
            {
                type: COLUMN_TYPES.TEXT,
                mapping: TAGS_FIELD,
                title: t`Tags`,
                sortable: false,
                formatter: formatTags,
                width: 300,
            },
            ...(agentType === AGENT_TYPE_GLOBAL
                ? [
                      {
                          type: COLUMN_TYPES.TEXT,
                          title: t`Assignment Type`,
                          mapping: 'agents',
                          sortable: true,
                          width: 185,
                          formatter: (value) => {
                              return getSimpleAssignmentType(value);
                          },
                      },
                  ]
                : []),
        ];
    }, [agentId, agentTags, agentType]);
}

function getActiveTests(tests) {
    return filter(tests, (test) => test[TEST_STATUS_FIELD] === TEST_STATUS_RUNNING);
}

function getSimpleAssignmentType(agentAssignment) {
    if (agentAssignment.all) {
        return 'All nPoints';
    }
    if (!isEmpty(agentAssignment.ids)) {
        return 'Selected nPoints';
    }
    if (!isEmpty(agentAssignment.tags)) {
        return 'Tagged nPoints';
    }
}

const emptyTestsText = i18n._(t`No service tests have been configured to run on this nPoint`);

const useStyles = makeStyles((theme) => ({
    customBadge: {
        backgroundColor: theme.palette.alert.warning,
        color: 'white',
    },
}));

function useTestsLoader(dataSource, agentType, agentTestDataDispatch) {
    const dataService = useDataService();
    const { errorWrapper } = useErrorWrapper();
    const [testCount, setTestCount] = useState(0);
    const [globalTestCount, setGlobalTestCount] = useState(0);

    const testsLoader = useMemo(() => {
        return async (loaderParams) => {
            const { url, countUrl, params, body } = dataSource;
            const loadParams = convertLoadParamsToQueryParams(loaderParams, false);
            const queryParams = merge({}, params, loadParams);

            try {
                const [testData, countData] = await Promise.all([
                    dataService.post(url, body, queryParams).then(getActiveTests),
                    dataService.post(countUrl, body, queryParams).then(getActiveTests),
                ]);

                setTestCount(countData.length);
                setGlobalTestCount(
                    agentType === AGENT_TYPE_GLOBAL ? filter(countData, 'runningOnGlobalnPoint').length : 0
                );

                agentTestDataDispatch({
                    tests: testData.map((test) => {
                        return {
                            name: test.name,
                            _id: test._id,
                            assignmentType: getSimpleAssignmentType(test.agents),
                            tags: test.agents.tags,
                            runningOnGlobalnPoint: test.runningOnGlobalnPoint,
                        };
                    }),
                });

                return {
                    length: testData.length,
                    rows: testData,
                };
            } catch (e) {
                errorWrapper(e);
            }
        };
    }, [dataSource, dataService, agentType, errorWrapper, agentTestDataDispatch]);

    return { testCount, globalTestCount, testsLoader };
}

const initialSort = [{ property: NAME_FIELD, direction: SORT_ASC }];

export function AgentDetailTests({ agentId, agentType, agentTags: npointTags, agentTestDataDispatch }) {
    const testsDataSource = useMemo(() => {
        return {
            url: `/ipm/v1/admin/agents/${agentId}/testsSearchable`,
            countUrl: `/ipm/v1/admin/agents/${agentId}/tests`,
            params: { searchFields: [NAME_FIELD, TAGS_FIELD] },
            // Sending undefined if there are no npointTags imitates the default behavior of the old GET route
            body: { tags: isEmpty(npointTags) ? undefined : npointTags },
        };
    }, [agentId, npointTags]);
    const { testCount, globalTestCount, testsLoader } = useTestsLoader(
        testsDataSource,
        agentType,
        agentTestDataDispatch
    );
    const testColumns = useTestColumns(agentId, agentType, npointTags);
    const classes = useStyles();
    const globalnPointOverloaded = testCount > AGENT_MAX_GLOBAL_TESTS && globalTestCount >= AGENT_MAX_GLOBAL_TESTS;
    const globalnPointHelpText = t`A maximum of ${AGENT_MAX_GLOBAL_TESTS} tests can be run on each Global nPoint. Tests will be prioritized based on the Run On nPoints setting for each test: (1) Run on select nPoints, then (2) Run on tagged nPoints, and finally (3) Run on all nPoints.`;

    return (
        <FormSection
            title={
                <RowFlexLayout vCenter>
                    <Box>
                        <Trans>Running Service Tests </Trans>
                    </Box>
                    {!globalnPointOverloaded && (
                        <Box ml={3}>
                            <Badge color="secondary" badgeContent={testCount} max={1000} showZero />
                        </Box>
                    )}
                    {globalnPointOverloaded && (
                        <Box ml={3}>
                            <Badge
                                classes={{ badge: classes.customBadge }}
                                badgeContent={AGENT_MAX_GLOBAL_TESTS}
                                max={1000}
                                showZero
                            />
                        </Box>
                    )}
                    {agentType === AGENT_TYPE_GLOBAL && (
                        <Box mt={1} ml={3}>
                            <NgpFieldHelpTip helpText={globalnPointHelpText} />
                        </Box>
                    )}
                </RowFlexLayout>
            }
        >
            <Box mt={4}>
                <DataTable
                    tableId={`${AGENTS_FUNCTION_NAME}-tests`}
                    loader={testsLoader}
                    columns={testColumns}
                    visibleRows={10}
                    sorters={initialSort}
                    disableCSVExport
                    disableColumnRemover
                    emptyText={emptyTestsText}
                />
            </Box>
        </FormSection>
    );
}
